import React, { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { store } from './store/store'
import { AuthProvider, useAuth } from './context/AuthContext'
import { usePageTracking } from './hooks/useGA4'
import { TourProvider } from './context/TourContext'
import { SnackbarProvider } from './context/SnackbarContext'
import { ThemeProvider } from './context/ThemeContext'
import { SidebarProvider } from './context/SidebarContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Navbar } from './components/Navbar'
import { Footer } from './components/Footer'
import AdminRoute from './components/admin/AdminRoute'
import { withFeatureGate } from './components/FeatureGate'
import { SessionExpiryHandler } from './components/SessionExpiryHandler'
import { ScrollToTop } from './components/ScrollToTop'

/** Wraps children in ErrorBoundary keyed by route — resets error state on navigation */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
}

// Helper for lazy-loading named exports
const lazyNamed = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ [key: string]: T }>,
  name: string
) => React.lazy(() => factory().then(m => ({ default: (m as any)[name] })))

// Route-level code-splitting: each page loads in its own chunk
const LoginPage = lazyNamed(() => import('./pages/LoginPage'), 'LoginPage')
const ForgotPasswordPage = lazyNamed(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage')
const VerifyEmailPage = lazyNamed(() => import('./pages/VerifyEmailPage'), 'VerifyEmailPage')
const ChangePasswordPage = lazyNamed(() => import('./pages/ChangePasswordPage'), 'ChangePasswordPage')
const HomePage = lazyNamed(() => import('./pages/HomePage'), 'HomePage')
const DashboardLayout = lazyNamed(() => import('./pages/DashboardLayout'), 'DashboardLayout')
const DashboardPage = lazyNamed(() => import('./pages/DashboardPage'), 'DashboardPage')
const DealsListPage = lazyNamed(() => import('./pages/DealsListPage'), 'DealsListPage')
const DealDetailPage = React.lazy(() => import('./pages/DealDetailPage').then(m => ({ default: m.DealDetailPage })))
const DealPreviewPage = lazyNamed(() => import('./pages/DealPreviewPage'), 'DealPreviewPage')
const MyDealsPage = lazyNamed(() => import('./pages/MyDealsPage'), 'MyDealsPage')
const NotificationsPage = lazyNamed(() => import('./pages/NotificationsPage'), 'NotificationsPage')
const ProfilePage = lazyNamed(() => import('./pages/ProfilePage'), 'ProfilePage')
const BillingPage = lazyNamed(() => import('./pages/BillingPage'), 'BillingPage')
const FeedbackPage = lazyNamed(() => import('./pages/FeedbackPage'), 'FeedbackPage')
const SettingsPage = lazyNamed(() => import('./pages/SettingsPage'), 'SettingsPage')
const AssistantPage = lazyNamed(() => import('./pages/AssistantPage'), 'AssistantPage')
const PricingPage = React.lazy(() => import('./pages/PricingPage'))
const SuccessPage = React.lazy(() => import('./pages/SuccessPage'))
const CreditsPage = lazyNamed(() => import('./pages/CreditsPage'), 'CreditsPage')
const TermsPage = lazyNamed(() => import('./pages/TermsPage'), 'TermsPage')
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'), 'PrivacyPage')
const CookiePolicyPage = lazyNamed(() => import('./pages/CookiePolicyPage'), 'CookiePolicyPage')
const DisclaimerPage = lazyNamed(() => import('./pages/DisclaimerPage'), 'DisclaimerPage')
const FAQPage = lazyNamed(() => import('./pages/FAQPage'), 'FAQPage')
const AboutPage = lazyNamed(() => import('./pages/AboutPage'), 'AboutPage')
const ContactPage = lazyNamed(() => import('./pages/ContactPage'), 'ContactPage')
const FeaturesPage = lazyNamed(() => import('./pages/FeaturesPage'), 'FeaturesPage')
const PreAnalysedDealsPage = lazyNamed(() => import('./pages/features/PreAnalysedDealsPage'), 'PreAnalysedDealsPage')
const LocalAreaDataPage = lazyNamed(() => import('./pages/features/LocalAreaDataPage'), 'LocalAreaDataPage')
const DashboardInsightsPage = lazyNamed(() => import('./pages/features/DashboardInsightsPage'), 'DashboardInsightsPage')
const LandRegistryPage = lazyNamed(() => import('./pages/features/LandRegistryPage'), 'LandRegistryPage')
const InteractiveMapPage = lazyNamed(() => import('./pages/features/InteractiveMapPage'), 'InteractiveMapPage')
const FavouritesPage = lazyNamed(() => import('./pages/features/FavouritesPage'), 'FavouritesPage')
const PdfReportsPage = lazyNamed(() => import('./pages/features/PdfReportsPage'), 'PdfReportsPage')
const BTLAnalyzerPageRaw = lazyNamed(() => import('./pages/BTLAnalyzerPage'), 'BTLAnalyzerPage')
const BTLAnalyzerPage = withFeatureGate(BTLAnalyzerPageRaw, 'analyzer.page')
const PoliciesPage = lazyNamed(() => import('./pages/PoliciesPage'), 'PoliciesPage')
const ReferralPage = React.lazy(() => import('./pages/ReferralPage'))
const KnowledgeHubPage = lazyNamed(() => import('./pages/knowledge-hub/KnowledgeHubPage'), 'KnowledgeHubPage')
const ArticleListPage = lazyNamed(() => import('./pages/knowledge-hub/ArticleListPage'), 'ArticleListPage')
const ArticleDetailPage = lazyNamed(() => import('./pages/knowledge-hub/ArticleDetailPage'), 'ArticleDetailPage')
const ThreadListPage = lazyNamed(() => import('./pages/knowledge-hub/ThreadListPage'), 'ThreadListPage')
const ThreadDetailPage = lazyNamed(() => import('./pages/knowledge-hub/ThreadDetailPage'), 'ThreadDetailPage')
const PublicKnowledgeHubPage = lazyNamed(() => import('./pages/public/PublicKnowledgeHubPage'), 'PublicKnowledgeHubPage')
const PublicArticleListPage = lazyNamed(() => import('./pages/public/PublicArticleListPage'), 'PublicArticleListPage')
const PublicArticleDetailPage = lazyNamed(() => import('./pages/public/PublicArticleDetailPage'), 'PublicArticleDetailPage')
const NotFoundPage = lazyNamed(() => import('./pages/NotFoundPage'), 'NotFoundPage')

// Admin pages
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'))
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'))
const AdminUserManagement = React.lazy(() => import('./pages/admin/AdminUserManagement'))
const AdminCustomerManagement = React.lazy(() => import('./pages/admin/AdminCustomerManagement'))
const AdminNotificationsPage = lazyNamed(() => import('./pages/admin/NotificationsPage'), 'NotificationsPage')
const FeedbackManagementPage = React.lazy(() => import('./pages/admin/FeedbackManagementPage'))
const AdminBetaReferrals = React.lazy(() => import('./pages/admin/AdminBetaReferrals'))
const AdminPipelinePage = React.lazy(() => import('./pages/admin/AdminPipelinePage'))
const FeatureManagementPage = React.lazy(() => import('./pages/admin/FeatureManagementPage'))
const AdminSettingsPage = React.lazy(() => import('./pages/admin/AdminSettingsPage'))
const AdminSocialPostsPage = React.lazy(() => import('./pages/admin/AdminSocialPostsPage'))
const AdminKnowledgeHubPage = React.lazy(() => import('./pages/admin/AdminKnowledgeHubPage'))
const AdminArticleEditorPage = React.lazy(() => import('./pages/admin/AdminArticleEditorPage'))
const AdminThreadDetailPage = React.lazy(() => import('./pages/admin/AdminThreadDetailPage'))

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  if (!isAuthenticated) {
    // Redirect to login but save the attempted URL
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />
  }

  return <>{children}</>
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  // If user is already logged in, redirect to dashboard
  return isAuthenticated ? <Navigate to="/dashboard" /> : <>{children}</>
}

/**
 * Route wrapper for public website pages (Home, Pricing, FAQ, etc.)
 * Redirects authenticated users to dashboard - they should use dashboard for all actions
 */
function WebsiteRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  // Authenticated users should not access website pages - redirect to dashboard
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>
}

// Component to track page views - must be inside Router
function PageTracker() {
  usePageTracking()
  return null
}

function App() {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <ThemeProvider>
          <AuthProvider>
            <SidebarProvider>
              <SnackbarProvider>
                <TourProvider>
                  <Router>
                  <RouteErrorBoundary>
                  <ScrollToTop />
                  <PageTracker />
                  <SessionExpiryHandler />
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}>
                  <Routes>
          {/* Auth routes - only for unauthenticated users */}
          <Route path="/login" element={<PublicOnlyRoute><LoginPage /><Footer /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><LoginPage /><Footer /></PublicOnlyRoute>} />
          <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /><Footer /></PublicOnlyRoute>} />
          <Route path="/verify-email" element={<><VerifyEmailPage /><Footer /></>} />

          {/* Public website routes - redirect authenticated users to dashboard */}
          <Route path="/" element={<WebsiteRoute><Navbar /><HomePage /><Footer /></WebsiteRoute>} />
          <Route path="/pricing" element={<WebsiteRoute><Navbar /><PricingPage /><Footer /></WebsiteRoute>} />
          <Route path="/success" element={<WebsiteRoute><Navbar /><SuccessPage /><Footer /></WebsiteRoute>} />
          <Route path="/terms" element={<WebsiteRoute><Navbar /><TermsPage /><Footer /></WebsiteRoute>} />
          <Route path="/privacy" element={<WebsiteRoute><Navbar /><PrivacyPage /><Footer /></WebsiteRoute>} />
          <Route path="/cookies" element={<WebsiteRoute><Navbar /><CookiePolicyPage /><Footer /></WebsiteRoute>} />
          <Route path="/disclaimer" element={<WebsiteRoute><Navbar /><DisclaimerPage /><Footer /></WebsiteRoute>} />
          <Route path="/faq" element={<WebsiteRoute><Navbar /><FAQPage /><Footer /></WebsiteRoute>} />
          <Route path="/about" element={<WebsiteRoute><Navbar /><AboutPage /><Footer /></WebsiteRoute>} />
          <Route path="/contact" element={<WebsiteRoute><Navbar /><ContactPage /><Footer /></WebsiteRoute>} />
          <Route path="/features" element={<WebsiteRoute><Navbar /><FeaturesPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/pre-analysed-deals" element={<WebsiteRoute><Navbar /><PreAnalysedDealsPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/local-area-data" element={<WebsiteRoute><Navbar /><LocalAreaDataPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/dashboard-insights" element={<WebsiteRoute><Navbar /><DashboardInsightsPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/land-registry" element={<WebsiteRoute><Navbar /><LandRegistryPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/interactive-map" element={<WebsiteRoute><Navbar /><InteractiveMapPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/favourites" element={<WebsiteRoute><Navbar /><FavouritesPage /><Footer /></WebsiteRoute>} />
          <Route path="/features/pdf-reports" element={<WebsiteRoute><Navbar /><PdfReportsPage /><Footer /></WebsiteRoute>} />
          <Route path="/knowledge-hub" element={<><Navbar /><PublicKnowledgeHubPage /><Footer /></>} />
          <Route path="/knowledge-hub/:categorySlug" element={<><Navbar /><PublicArticleListPage /><Footer /></>} />
          <Route path="/knowledge-hub/:categorySlug/:articleSlug" element={<><Navbar /><PublicArticleDetailPage /><Footer /></>} />

          {/* Dashboard routes (with sidebar, no navbar/footer) */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            {process.env.REACT_APP_ASSISTANT_ENABLED === 'true' && (
              <Route path="assistant" element={<AssistantPage />} />
            )}
            <Route path="deals" element={<DealsListPage />} />
            <Route path="deals/:id" element={<DealDetailPage />} />
            <Route path="my-deals" element={<MyDealsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="deal-analyzer" element={<BTLAnalyzerPage />} />
            <Route path="analyzer" element={<BTLAnalyzerPage />} />
            <Route path="referrals" element={<ReferralPage />} />
            <Route path="change-password" element={<ChangePasswordPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="knowledge-hub" element={<KnowledgeHubPage />} />
            <Route path="knowledge-hub/articles" element={<ArticleListPage />} />
            <Route path="knowledge-hub/articles/:articleId" element={<ArticleDetailPage />} />
            <Route path="knowledge-hub/discussions" element={<ThreadListPage />} />
            <Route path="knowledge-hub/discussions/:threadId" element={<ThreadDetailPage />} />
            <Route path="policies" element={<PoliciesPage />} />
            <Route path="welcome" element={<SuccessPage />} />
          </Route>

          {/* Public deal preview (social media landing page - works for all users, auth and unauth) */}
          <Route path="/deals/preview/:id" element={<DealPreviewPage />} />

          {/* Public deal detail route (for homepage teasers - shows masked content) */}
          <Route
            path="/deals/:id"
            element={<WebsiteRoute><Navbar /><DealDetailPage showMasked={true} /><Footer /></WebsiteRoute>}
          />

          {/* Admin routes (separate from user dashboard) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUserManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <AdminRoute>
                <AdminCustomerManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <AdminRoute>
                <AdminNotificationsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/feedback"
            element={
              <AdminRoute>
                <FeedbackManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/beta-referrals"
            element={
              <AdminRoute>
                <AdminBetaReferrals />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/pipeline"
            element={
              <AdminRoute>
                <AdminPipelinePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/features"
            element={
              <AdminRoute>
                <FeatureManagementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/social-posts"
            element={
              <AdminRoute>
                <AdminSocialPostsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <AdminSettingsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/knowledge-hub"
            element={
              <AdminRoute>
                <AdminKnowledgeHubPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/knowledge-hub/articles/new"
            element={
              <AdminRoute>
                <AdminArticleEditorPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/knowledge-hub/threads/:threadId"
            element={
              <AdminRoute>
                <AdminThreadDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/knowledge-hub/articles/:articleId/edit"
            element={
              <AdminRoute>
                <AdminArticleEditorPage />
              </AdminRoute>
            }
          />

          {/* 404 Catch-all route */}
          <Route path="*" element={<><Navbar /><NotFoundPage /><Footer /></>} />
                  </Routes>
                  </Suspense>
                  </RouteErrorBoundary>
                </Router>
                </TourProvider>
              </SnackbarProvider>
            </SidebarProvider>
          </AuthProvider>
        </ThemeProvider>
      </Provider>
    </HelmetProvider>
  )
}

export default App
